/**
 * Grounded explanations: every statement here is computed from the notes,
 * key and chords passed in (AGENT.md principle 4). No free-form claims.
 */
import { CHORD_DESCRIPTION, CHORD_SUFFIX, detectChord, inversionName } from "./chords";
import { diatonicChord, diatonicChords, type Degree, type HarmonicFunction } from "./diatonic";
import { keyName, keyScale, keySignature, relativeKey, spellInKey, type MusicalKey } from "./key";
import { intervalName, octaveOf, pitchClass } from "./notes";

export interface NoteExplanation {
  names: string[]; // spelled in key context, with octave
  intervals: { from: string; to: string; semitones: number; name: string }[]; // between adjacent notes
  chord: null | { symbol: string; description: string; inversion: string; bass: string; roman: string | null };
  outsideKey: string[]; // note names not in the key's scale
  summary: string;
}

const FUNCTION_TEXT: Record<HarmonicFunction, string> = {
  tonic: "tonic function (stable, sounds like home)",
  predominant: "predominant function (moves away from home, often leads to the dominant)",
  dominant: "dominant function (creates tension that wants to resolve to the tonic)",
};

export function explainNotes(midiNotes: readonly number[], key: MusicalKey): NoteExplanation {
  const sorted = [...new Set(midiNotes)].sort((a, b) => a - b);
  const names = sorted.map((n) => `${spellInKey(n, key)}${octaveOf(n)}`);
  const intervals = sorted.slice(1).map((n, i) => ({
    from: names[i]!, to: names[i + 1]!, semitones: n - sorted[i]!, name: intervalName(n - sorted[i]!),
  }));
  const scalePcs = new Set(keyScale(key).pitchClasses);
  const outsideKey = sorted.filter((n) => !scalePcs.has(pitchClass(n))).map((n) => spellInKey(n, key));

  let chord: NoteExplanation["chord"] = null;
  const det = detectChord(sorted);
  if (det) {
    const root = spellInKey(det.rootPc, key);
    const bass = spellInKey(det.bassPc, key);
    const symbol = root + CHORD_SUFFIX[det.quality] + (det.inversion > 0 ? `/${bass}` : "");
    // Is it diatonic? Compare against triads and sevenths of the key.
    const match = [...diatonicChords(key), ...diatonicChords(key, true)].find(
      (c) => c.rootPc === det.rootPc && c.quality === det.quality,
    );
    chord = {
      symbol,
      description: `${root} ${CHORD_DESCRIPTION[det.quality]}`,
      inversion: inversionName(det.inversion),
      bass,
      roman: match ? match.roman : null,
    };
  }

  let summary: string;
  if (sorted.length === 0) summary = "No notes.";
  else if (sorted.length === 1) summary = `${names[0]} — ${outsideKey.length ? `not in ${keyName(key)}` : `in ${keyName(key)}`}.`;
  else if (sorted.length === 2) summary = `${names[0]} to ${names[1]}: ${intervals[0]!.name}.`;
  else if (chord) {
    summary = `${chord.description}, ${chord.inversion}` +
      (chord.roman ? ` — the ${chord.roman} chord in ${keyName(key)}.` : ` — not diatonic to ${keyName(key)}.`);
  } else summary = `${sorted.length} notes; no standard triad or seventh chord matches exactly.`;

  return { names, intervals, chord, outsideKey, summary };
}

export interface KeyExplanation {
  name: string;
  scaleNotes: string[];
  signature: string;
  relative: string;
  chords: { roman: string; symbol: string; function: HarmonicFunction }[];
}

export function explainKey(key: MusicalKey): KeyExplanation {
  const sig = keySignature(key);
  const rel = relativeKey(key);
  return {
    name: keyName(key),
    scaleNotes: keyScale(key).notes,
    signature: sig === 0 ? "no sharps or flats" : sig > 0 ? `${sig} sharp${sig > 1 ? "s" : ""}` : `${-sig} flat${sig < -1 ? "s" : ""}`,
    relative: `${keyName(rel)} (same notes, different tonal centre)`,
    chords: diatonicChords(key).map((c) => ({ roman: c.roman, symbol: c.symbol, function: c.function })),
  };
}

export function explainDegree(key: MusicalKey, degree: Degree, seventh = false): string {
  const c = diatonicChord(key, degree, seventh);
  const tonic = keyScale(key).notes[0]!;
  let extra = "";
  if (key.mode === "major" && degree === 5) {
    const leading = keyScale(key).notes[6]!;
    extra = ` It contains ${leading}, the leading tone, a semitone below ${tonic}.`;
  }
  if (degree === 1) extra = ` ${tonic} is the tonal centre of ${keyName(key)}.`;
  return `${c.symbol} (${c.roman}) is built on scale degree ${degree} of ${keyName(key)}: ${c.notes.join("–")}. ` +
    `It is a ${CHORD_DESCRIPTION[c.quality]} with ${FUNCTION_TEXT[c.function]}.${extra}`;
}
